<script>
import { BFormRadio } from 'bootstrap-vue'
import Overlay from "@core/components/common/Overlay.vue";
import SearchSubstanceInList from "@/views/product/components/product-card/SearchSubstanceInList.vue";
import {HamletStaticTable} from "@core/components/common";

export default {
    components: {Overlay, SearchSubstanceInList, BFormRadio, HamletStaticTable},
    props: {
        list: {
            type: Object,
            required: true,
        },
        includedSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        excludedSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        readOnly: {
            default: false
        },
        readOnlyForIncluded: {
            default: false
        },
        includedRadioDisabled: {
            default: false
        },
        includedRadioTitle: {
            default: null,
        }
    },
    data() {
        return {
            showLoading: false,
            listSubstancesLoaded: false,
            listSubstancesLoading: false,
            listSubstances: [],
            includedInList: [],
            excludedInList: [],
            columns: [
                {key: 'title', label: this.$t('Component(SubstanceTable):column-title')},
                {key: 'cas', label: this.$t('Component(SubstanceTable):column-cas')},
                {key: 'ec', label: this.$t('Component(SubstanceTable):column-ec')},
                {key: 'included', label: this.$t('Component(SubstanceTable):column-included')},
                {key: 'not_included', label: this.$t('Component(SubstanceTable):column-not-included')},
                {key: 'actions', label: this.$t('Component(SubstanceTable):column-undefined')},
            ],
            includedRadioTitleValue: this.includedRadioTitle || this.$t('Component(SubstanceTable):hover-column-included')
        };
    },
    computed: {
        rows() {
            const seen = new Set();
            const items = [];

            this.includedInList.forEach(substance => {
                if (!substance || seen.has(substance.id)) {
                    return;
                }
                seen.add(substance.id);
                items.push({...substance, selected: true});
            });

            this.excludedInList.forEach(substance => {
                if (!substance || seen.has(substance.id)) {
                    return;
                }
                seen.add(substance.id);
                items.push({...substance, selected: false});
            });

            items.sort((a, b) => {
                if (a.id !== b.id) {
                    return a.id - b.id;
                }

                return (a.title || '').localeCompare(b.title || '');
            });

            return items;
        },
    },
    methods: {
        ensureListSubstancesLoaded() {
            if (this.listSubstancesLoaded || this.listSubstancesLoading) {
                return Promise.resolve(this.listSubstances);
            }

            this.showLoading = true;
            this.listSubstancesLoading = true;
            return this.list.domain.getSubstances()
                .then(substances => {
                    this.listSubstances = substances || [];
                    this.listSubstancesLoaded = true;
                    return this.listSubstances;
                })
                .finally(() => {
                    this.listSubstancesLoading = false;
                    this.showLoading = false;
                });
        },
        refresh() {
            const included = this.includedSubstances.map(s => s.substance);
            const excluded = this.excludedSubstances.map(s => s.substance);

            return Promise
                .all([
                    this.list.domain.extractIncluded(included),
                    this.list.domain.extractIncluded(excluded),
                ])
                .then(([includedInList, excludedInList]) => {
                    this.includedInList = includedInList || [];
                    this.excludedInList = excludedInList || [];
                });
        },
        isSelected(substance) {
            return this.rows.some(row => row.id === substance.id);
        },
        isInCurrentList(substance) {
            return this.listSubstances.some(item => item.id === substance.id);
        },
        includeSubstanceToAList(substance) {
            if (this.isInCurrentList(substance) || !this.list.domain.allowNewSubstances()) {
                this.$emit('include', substance);
                this.refresh();
                return;
            }

            this.$emit('includeSubstanceToAList', substance, null, this.list, () => {
                this.$emit('include', substance);
                this.refresh();
            });
        },
        includeRow(substance) {
            substance.selected = true;
            this.$emit('include', substance);
        },
        excludeRow(substance) {
            substance.selected = false;
            this.$emit('exclude', substance);
        },
        undefineRow(substance) {
            substance.selected = null;
            this.$emit('undefined', substance);
        },
    },
    watch: {
        includedSubstances() {
            this.refresh();
        },
        excludedSubstances() {
            this.refresh();
        },
    },
    created() {
        this.refresh();
        this.ensureListSubstancesLoaded();
    },
}
</script>

<template>
    <Overlay :show="showLoading">
        <div class="search-only-wrap w-100">
            <HamletStaticTable
                :columns="columns"
                :items="rows"
                :striped="false"
                :bordered="true"
                empty-mode="blank"
                card-class="search-only-table-card pt-0 mt-0"
            >
                <template #cell-cas="{ item }">
                    <div class="substance-min-width">{{ item.cas }}</div>
                </template>
                <template #cell-ec="{ item }">
                    <div class="substance-min-width">{{ item.ec }}</div>
                </template>
                <template #cell-included="{ item }">
                    <div class="text-center" :title="includedRadioTitleValue">
                        <b-form-radio
                            :disabled="readOnly || includedRadioDisabled || (readOnlyForIncluded && item.selected)"
                            v-model="item.selected"
                            :name="'search-only-substance-' + list.id + '-' + item.id"
                            :value="true"
                            class="d-inline-block"
                            @change="includeRow(item)"
                        />
                    </div>
                </template>
                <template #cell-not_included="{ item }">
                    <div class="text-center" :title="$t('Component(SubstanceTable):hover-column-not-included')">
                        <b-form-radio
                            :disabled="readOnly || (readOnlyForIncluded && item.selected)"
                            v-model="item.selected"
                            :name="'search-only-substance-' + list.id + '-' + item.id"
                            :value="false"
                            class="d-inline-block"
                            @change="excludeRow(item)"
                        />
                    </div>
                </template>
                <template #cell-actions="{ item }">
                    <div class="text-center" :title="$t('Component(SubstanceTable):hover-column-undefined')">
                        <b-form-radio
                            :disabled="readOnly || (readOnlyForIncluded && item.selected)"
                            v-model="item.selected"
                            :name="'search-only-substance-' + list.id + '-' + item.id"
                            :value="null"
                            class="d-inline-block"
                            @change="undefineRow(item)"
                        />
                    </div>
                </template>
                <template #no-records>
                    No selected substances in this list.
                </template>
            </HamletStaticTable>

            <SearchSubstanceInList
                v-if="!readOnly"
                class="p-2"
                :substances="listSubstances"
                :loading="listSubstancesLoading"
                :is-selected="isSelected"
                :allow-add-new="list.domain.allowNewSubstances()"
                :disabled="readOnly"
                :input-label="list.domain.allowNewSubstances() ? $t('Component(Button):open-search-or-add-substance') : $t('Component(Composition):search-label')"
                @include="includeSubstanceToAList"
            />
        </div>
    </Overlay>
</template>

<style lang="scss">
.substance-min-width {
    min-width: 115px;
}

.search-only-wrap .search-only-table-card {
    border: 0 !important;
    box-shadow: none !important;
}

.search-only-wrap .search-only-table-card .card-header {
    padding-top: 0 !important;
}
</style>
